import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, ActivatedRoute } from '@angular/router';
import { ApiService } from '@app/core/services/API/api-service';
import { Title, Meta } from '@angular/platform-browser';
import { CustomPaginatorComponent } from '@app/shared/components/custom-paginator/custom-paginator';
import { Spinner } from '@app/shared/components/spinner/spinner';
import { Subject, takeUntil, finalize } from 'rxjs';
import { AddPeopleForm } from '@app/pages/people/add-people-form/add-people-form';
import { EditPeopleForm } from '@app/pages/people/edit-people-form/edit-people-form';
import { ConfirmExport } from '@app/shared/components/confirm-export/confirm-export';
import { ConfirmPopup } from '@app/shared/components/confirm-popup/confirm-popup';
import { ToastrService } from 'ngx-toastr';
export interface AccessLevel {
  _id: string;
  description: string;
  accessDetails: any[];
  accesslevel_name: string;
  createdAt: string;
  createdBy: string;
  default: boolean;
  en_id: string;
  liscense_name: string;
  liscense_type: string;
  status: string;
  updatedAt: string;
  __v: number;
}

export interface Person {
  _id: string;
  name: string;
  email: string;
  image: string;
  phone: string;
  homeTeamID: string;
  jobInfo: string;
  jobRoleID: string;
  timezone: string;
  managerID: string;
  utype: string;
  accesslevel: AccessLevel;
  isActive: boolean;
  isOnline?: boolean;
  status: string;
  createdBy: string;
  lastUpdatedByID?: string;
  createdAt: string;
  updatedAt: string;
  address: string;
}

export interface TableHeader {
  fieldName: string;
  prop: string;
}

export interface TableHeaders {
  _id: string;
  list: TableHeader[];
  moduleName: string;
  objCode: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface PeopleApiResponse {
  success: boolean;
  message: string;
  data: {
    tableHeaders: TableHeaders;
    userDetails: Person[];
  };
}

@Component({
  selector: 'app-people-listing',
  imports: [CommonModule, FormsModule, MatIconModule, MatSortModule, MatTableModule, MatTooltipModule, CustomPaginatorComponent, Spinner, AddPeopleForm, EditPeopleForm, ConfirmExport,ConfirmPopup],
  templateUrl: './people-listing.html',
  styleUrl: './people-listing.scss'
})
export class PeopleListing implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Angular 20 signals for state management
  people = signal<Person[]>([]);
  tableHeaders = signal<TableHeader[]>([]);
  loading = signal(false);
  error = signal<string | null>(null);
  searchTerm = signal('');
  searchText = signal('');
  selectedPeople = signal<string[]>([]);
  statusFilter = signal<'active' | 'inactive'>('active');

  // Data source for Material table
  dataSource: Person[] = [];
  paginatedDataSource: Person[] = [];

  // Slider state signals
  showAddPeopleSlider = signal(false);
  showEditPeopleSlider = signal(false);
  selectedPersonForEdit = signal<Person | null>(null);

  // Export modal state
  showExportModal = signal(false);
  //Delete
  showDeleteConfirmPopup = signal(false);
  deleteLoading = signal(false);
//Deactivate
  showDeactivateConfirmPopup = signal(false);
  deactivateLoading = signal(false);
//Activate
  showActivateConfirmPopup = signal(false);
  activateLoading = signal(false);

  // Pagination signals
  currentPage = signal(1);
  pageSize = signal(10);
  totalItems = signal(0);
  // Sort
  currentSortColumn = signal<string | null>(null);
  currentSortDirection = signal<'asc' | 'desc'>('asc');
  private toastr = inject(ToastrService);

  constructor(
    private apiService: ApiService, 
    private router: Router,
    private route: ActivatedRoute,
    private titleService: Title,
    private metaService: Meta
  ) {
    // Set up effect to reset pagination when search term changes
    effect(() => {
      const term = this.searchTerm();
      if (term) {
        this.currentPage.set(1);
      }
    });
  }

  ngOnInit(): void {
    this.loadPeople();

    // For meta title and description
    this.route.data.subscribe((data: { [key: string]: any }) => {
      if (data['title']) {
        this.titleService.setTitle(data['title']);
      }

      if (data['description']) {
        this.metaService.updateTag({
          name: 'description',
          content: data['description']
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Computed properties using Angular 20 signals
  filteredPeople = computed(() => {
    const people = this.people();
    const term = this.searchTerm().toLowerCase().trim();
    const status = this.statusFilter();

    let filtered = people;

    filtered = filtered.filter(person => {
      if (status === 'active') {
        return person.isActive === true;
      } else {
        return person.isActive === false;
      }
    });

    // Apply search filter
    if (term) {
      filtered = filtered.filter(person =>
        person.name.toLowerCase().includes(term) ||
        person.email.toLowerCase().includes(term) ||
        (person.phone && person.phone.toLowerCase().includes(term)) ||
        (person.address && person.address.toLowerCase().includes(term)) ||
        (person.timezone && person.timezone.toLowerCase().includes(term))
      );
    }

    return filtered;
  });

  paginatedPeople = computed(() => {
    const filtered = this.filteredPeople();
    const page = this.currentPage();
    const size = this.pageSize();
    const startIndex = (page - 1) * size;
    const endIndex = startIndex + size;

    return filtered.slice(startIndex, endIndex);
  });

  totalPages = computed(() => {
    return Math.ceil(this.filteredPeople().length / this.pageSize());
  });

  hasSelection = computed(() => this.selectedPeople().length > 0);
  isAllSelected = computed(() => {
    const filtered = this.paginatedPeople();
    const selected = this.selectedPeople();
    return filtered.length > 0 && filtered.every(person => selected.includes(person._id));
  });
  isIndeterminate = computed(() => {
    const filtered = this.paginatedPeople();
    const selected = this.selectedPeople();
    return selected.length > 0 && selected.length < filtered.length;
  });

  hasSelectedActivePeople = computed(() => {
    const filteredPeople = this.filteredPeople();
    return this.selectedPeople().some(id => filteredPeople.find(p => p._id === id)?.isActive === true);
  });
  hasSelectedInactivePeople = computed(() => {
    const filteredPeople = this.filteredPeople();
    return this.selectedPeople().some(id => filteredPeople.find(p => p._id === id)?.isActive === false);
  });

  // Combined loading state for page overlay
  isPageLoading = computed(() => 
    this.loading() || 
    this.deleteLoading() || 
    this.deactivateLoading() || 
    this.activateLoading()
  );

  loadPeople(): void {
    this.loading.set(true);
    this.error.set(null);

    this.apiService.getAllPeople()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: (response: PeopleApiResponse) => {
          if (response.success) {
            this.people.set(response.data.userDetails);
            this.tableHeaders.set(response.data.tableHeaders.list);
            this.totalItems.set(response.data.userDetails.length);
            this.updateDataSource();
          } else {
            this.error.set(response.message || 'Failed to load people data');
          }
        },
        error: (error) => {
          console.error('Error loading people:', error);
          this.error.set('Failed to load people data. Please try again.');
        }
      });
  }

  getFieldValue(person: Person, prop: string): any {
    if (prop === 'accesslevel') {
      return person.accesslevel?.accesslevel_name || person.utype;
    }
    return (person as any)[prop];
  }

   getStatusClass(status: string): string {
    if (status === 'online') {
      return 'status-online';
    } else if (status === 'offline') {
      return 'status-offline';
    }
    return status === 'active' ? 'status-active' : 'status-inactive';
  }

  onSearchChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm.set(target.value);
  }

  refreshData(): void {
    this.loadPeople();
  }

  trackByPersonId(index: number, person: Person): string {
    return person._id;
  }

  // Checkbox functionality
  isPersonSelected(personId: string): boolean {
    return this.selectedPeople().includes(personId);
  }

  togglePersonSelection(personId: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    const currentSelected = this.selectedPeople();

    if (target.checked) {
      if (!currentSelected.includes(personId)) {
        this.selectedPeople.set([...currentSelected, personId]);
      }
    } else {
      this.selectedPeople.set(currentSelected.filter(id => id !== personId));
    }
  }

  toggleAllSelection(event: Event): void {
    const target = event.target as HTMLInputElement;
    const filtered = this.paginatedPeople();

    if (target.checked) {
      this.selectedPeople.set(filtered.map(person => person._id));
    } else {
      this.selectedPeople.set([]);
    }
  }

  clearSelection(): void {
    this.selectedPeople.set([]);
  }

  // Pagination methods
  onPageChange(page: number): void {
    this.currentPage.set(page);
    this.updatePaginatedDataSource();
  }

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.currentPage.set(1);
    this.updatePaginatedDataSource();
  }

  retryLoading(): void {
    this.loadPeople();
  }

  goBack(): void {
    // Implement navigation back logic
    window.history.back();
  }

  navigateToTeams(): void {
    this.router.navigate(['/people/teams']);
  }

  navigateToPersonView(personId: string): void {
    this.router.navigate(['/people/peopleview', personId]);
  }

  getDisplayedColumns(): string[] {
    return ['select', ...this.tableHeaders().map(h => h.prop)];
  }

  // Search methods
  onSearchInput(): void {
    this.searchTerm.set(this.searchText());
    this.updateDataSource();
  }

  onSearch(): void {
    this.searchTerm.set(this.searchText());
    this.updateDataSource();
  }

  clearSearch(): void {
    this.searchText.set('');
    this.searchTerm.set('');
    this.updateDataSource();
  }

  setStatusFilter(status: 'active' | 'inactive'): void {
    this.statusFilter.set(status);
    this.currentPage.set(1); // Reset to first page when filter changes
    this.updateDataSource();
  }

  toggleStatusFilter(): void {
    const currentStatus = this.statusFilter();
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    this.setStatusFilter(newStatus);
  }

  // Data check methods
  hasData(): boolean {
    return this.people().length > 0;
  }

  hasSelectedPeople(): boolean {
    return this.selectedPeople().length > 0;
  }

  hasSingleSelectedPeople(): boolean {
    return this.selectedPeople().length === 1;
  }

  hasMultipleSelectedPeople(): boolean {
    return this.selectedPeople().length > 1;
  }

  // Action methods
  openAddPeopleSliderWithoutParent(): void {
    this.showAddPeopleSlider.set(true);
  }

  closeAddPeopleSlider(): void {
    this.showAddPeopleSlider.set(false);
  }

  closeEditPeopleSlider(): void {
    this.showEditPeopleSlider.set(false);
    this.selectedPersonForEdit.set(null);
  }

  handlePersonSubmitted(data: any): void {
    // Handle the submitted data from the add form
    console.log('Person submitted:', data);

    // Optimistic update - add the new person to the current list
    const currentPeople = this.people();
    const newPerson: Person = {
      _id: data._id || this.generateId(),
      name: data.name,
      email: data.email,
      image: data.image || '',
      phone: data.phone,
      homeTeamID: data.homeTeamID,
      jobInfo: data.jobInfo,
      jobRoleID: data.jobRoleID,
      timezone: data.timezone,
      managerID: data.managerID,
      utype: data.utype || 'user',
      accesslevel: data.accesslevel,
      isActive: data.isActive !== false,
      isOnline: data.isOnline !== undefined ? data.isOnline : false,
      status: data.isActive ? 'active' : 'inactive',
      createdBy: data.createdBy || '',
      lastUpdatedByID: data.lastUpdatedByID,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      address: data.address || ''
    };

    // Add to the beginning of the list
    this.people.set([newPerson, ...currentPeople]);
    this.totalItems.set(this.people().length);
    this.updateDataSource();

    this.closeAddPeopleSlider();
  }

  handlePersonUpdated(data: any): void {
    // Handle the updated data from the edit form
    console.log('Person updated:', data);

    // Optimistic update - update the person in the current list
    const currentPeople = this.people();
    const updatedPeople = currentPeople.map(person =>
      person._id === data._id ? { ...person, ...data, updatedAt: new Date().toISOString() } : person
    );

    this.people.set(updatedPeople);
    this.updateDataSource();

    this.closeEditPeopleSlider();
  }

  openExportModal(): void {
    this.showExportModal.set(true);
  }

  closeExportModal(): void {
    this.showExportModal.set(false);
  }

  onExportConfirm(data: { emails: string[] }): void {
    // Call the API service to export people
    this.apiService.exportPeople(data.emails)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.closeExportModal())
      )
      .subscribe({
        next: (response) => {
          console.log('Export successful:', response);
          // The confirm-export component will show success message
        },
        error: (error) => {
          console.error('Export failed:', error);
          // Handle error if needed
        }
      });
  }

  openEditPeopleSlider(): void {
    const selectedPersonId = this.selectedPeople()[0];
    const selectedPerson = this.people().find(person => person._id === selectedPersonId);
    if (selectedPerson) {
      this.selectedPersonForEdit.set(selectedPerson);
      this.showEditPeopleSlider.set(true);
    }
  }

  openEditMultiplePeopleForm(): void {
    console.log('Open edit multiple people form for:', this.selectedPeople());
    // Implement edit multiple people functionality
  }

  openDeletePeoplePopup(): void {
    if (this.selectedPeople().length > 0) {
      this.showDeleteConfirmPopup.set(true);
    }
  }

  showConfirmSharePeoplePopup(): void {
    console.log('Show share people popup for:', this.selectedPeople());
    // Implement share people functionality
  }

  addNewPerson(): void {
    // Navigate to add people form or open modal
    // This would typically navigate to the add-people-form component
    console.log('Navigate to add people form');
    // You can implement navigation logic here
    // this.router.navigate(['/people/add']);
  }

  editSelectedPeople(): void {
    // Navigate to edit people form or open modal for selected people
    const selectedIds = this.selectedPeople();
    console.log('Edit selected people:', selectedIds);
    // You can implement navigation logic here
    // this.router.navigate(['/people/edit'], { queryParams: { ids: selectedIds.join(',') } });
  }


  deleteSelectedPeople(): void {
    const selectedIds = this.selectedPeople();
    if (selectedIds.length === 0) {
      return;
    }

    // Store original data for rollback
    const originalPeople = [...this.people()];

    // Optimistic update - remove selected people from the list
    const remainingPeople = this.people().filter(person => !selectedIds.includes(person._id));
    this.people.set(remainingPeople);
    this.totalItems.set(this.people().length);
    this.updateDataSource();

    this.deleteLoading.set(true);
    this.apiService.deletePeople(selectedIds)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.deleteLoading.set(false))
      )
      .subscribe({
        next: (response) => {
          console.log('Delete successful:', response);
          this.selectedPeople.set([]);
          this.closeDeleteConfirmPopup();
        },
        error: (error) => {
          console.error('Delete failed:', error);
          // Rollback on error
          this.people.set(originalPeople);
          this.totalItems.set(this.people().length);
          this.updateDataSource();
          this.error.set('Failed to delete people. Please try again.');
        }
      });
  }

  closeDeleteConfirmPopup(): void {
    this.showDeleteConfirmPopup.set(false);
  }

  onDeleteConfirm(): void {
    this.deleteSelectedPeople();
  }

  onDeleteCancel(): void {
    this.closeDeleteConfirmPopup();
  }

   // Deactivate methods
   openDeactivatePeoplePopup(): void {
    if (this.selectedPeople().length > 0) {
      this.showDeactivateConfirmPopup.set(true);
    }
  }

  deactivateSelectedPeople(): void {
    const selectedIds = this.selectedPeople();
    if (selectedIds.length === 0) {
      return;
    }

    // Store original data for rollback
    const originalPeople = [...this.people()];

    // Optimistic update - update people status to inactive
    const updatedPeople = this.people().map(person =>
      selectedIds.includes(person._id)
        ? { ...person, isActive: false, status: 'inactive', updatedAt: new Date().toISOString() }
        : person
    );
    this.people.set(updatedPeople);
    this.updateDataSource();

    this.deactivateLoading.set(true);
    this.apiService.deactivatePeople(selectedIds)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.deactivateLoading.set(false))
      )
      .subscribe({
        next: (response) => {
          console.log('Deactivate successful:', response);
          this.toastr.success(response.message || 'Person Deactivate successfully!');
          this.selectedPeople.set([]);
          this.closeDeactivateConfirmPopup();
        },
        error: (error) => {
          console.error('Deactivate failed:', error);
          // Rollback on error
          this.people.set(originalPeople);
          this.updateDataSource();
          this.toastr.error(error.message || 'Failed to deactivate people. Please try again.');
          this.error.set('Failed to deactivate people. Please try again.');
        }
      });
  }

  closeDeactivateConfirmPopup(): void {
    this.showDeactivateConfirmPopup.set(false);
  }

  onDeactivateConfirm(): void {
    this.deactivateSelectedPeople();
  }

  onDeactivateCancel(): void {
    this.closeDeactivateConfirmPopup();
  }

  // Activate methods
  openActivatePeoplePopup(): void {
    if (this.selectedPeople().length > 0) {
      this.showActivateConfirmPopup.set(true);
    }
  }

  activateSelectedPeople(): void {
    const selectedIds = this.selectedPeople();
    if (selectedIds.length === 0) {
      return;
    }

    // Store original data for rollback
    const originalPeople = [...this.people()];

    // Optimistic update - update people status to active
    const updatedPeople = this.people().map(person =>
      selectedIds.includes(person._id)
        ? { ...person, isActive: true, status: 'active', updatedAt: new Date().toISOString() }
        : person
    );
    this.people.set(updatedPeople);
    this.updateDataSource();

    this.activateLoading.set(true);
    this.apiService.activatePeople(selectedIds)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.activateLoading.set(false))
      )
      .subscribe({
        next: (response) => {
          console.log('Activate successful:', response);
          this.toastr.success(response.message || 'Person Activate successfully!');
          this.selectedPeople.set([]);
          this.closeActivateConfirmPopup();
        },
        error: (error) => {
          console.error('Activate failed:', error);
          // Rollback on error
          this.people.set(originalPeople);
          this.updateDataSource();
          this.toastr.error(error.message || 'Failed to activate people. Please try again.');
          this.error.set('Failed to activate people. Please try again.');
        }
      });
  }

  closeActivateConfirmPopup(): void {
    this.showActivateConfirmPopup.set(false);
  }

  onActivateConfirm(): void {
    this.activateSelectedPeople();
  }

  onActivateCancel(): void {
    this.closeActivateConfirmPopup();
  }

  // Update data source based on current filters
  updateDataSource(): void {
    this.dataSource = this.filteredPeople();
    this.updatePaginatedDataSource();
  }

  // Update paginated data source
  updatePaginatedDataSource(): void {
    const page = this.currentPage();
    const size = this.pageSize();
    const startIndex = (page - 1) * size;
    const endIndex = startIndex + size;

    this.paginatedDataSource = this.dataSource.slice(startIndex, endIndex);
  }

  // Generate a unique ID for new people
  private generateId(): string {
    return 'person_' + Math.random().toString(36).substr(2, 9);
  }

  // Get initials from person name for avatar placeholder
  getInitials(name: string): string {
    if (!name || name.trim() === '') {
      return '?';
    }

    const words = name.trim().split(' ');
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }

    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  }

  // Handle image loading errors
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';

    // Find and show the avatar placeholder
    const avatarContainer = img.closest('.person-avatar') as HTMLElement;
    if (avatarContainer) {
      const placeholder = avatarContainer.querySelector('.avatar-placeholder') as HTMLElement;
      if (placeholder) {
        placeholder.style.display = 'flex';
      }
    }
  }

  getAvatarColor(userId: string): string {
    // Generate a consistent color based on user ID - Rich vibrant colors matching updates component
    const colors = [
      '#0644C9', // Rich Vibrant Blue
      '#8B2CF5', // Rich Purple
      '#E91E63', // Rich Pink
      '#4CAF50', // Rich Green
      '#FF9800', // Rich Orange
      '#9C27B0', // Rich Magenta
      '#3F51B5', // Rich Indigo
      '#00BCD4', // Rich Cyan
      '#795548', // Rich Brown
      '#2196F3', // Rich Blue
      '#009688', // Rich Teal
      '#673AB7', // Rich Deep Purple
      '#FF6F00', // Rich Deep Orange
      '#1976D2', // Rich Dark Blue
      '#F44336', // Rich Red
      '#FFC107', // Rich Amber
      '#8BC34A', // Rich Light Green
      '#00E676', // Rich Light Green
      '#18FFFF', // Rich Light Cyan
      '#E1BEE7', // Rich Light Purple
      '#FFCDD2', // Rich Light Pink
      '#B39DDB', // Rich Light Purple
      '#9FA8DA', // Rich Light Indigo
      '#90CAF9', // Rich Light Blue
      '#81D4FA', // Rich Light Blue
      '#80DEEA', // Rich Light Cyan
      '#80CBC4', // Rich Light Teal
      '#A5D6A7', // Rich Light Green
      '#C8E6C9', // Rich Light Green
      '#DCEDC8', // Rich Light Green
      '#F0F4C3', // Rich Light Lime
      '#FFF9C4', // Rich Light Yellow
      '#FFECB3', // Rich Light Orange
      '#FFE0B2', // Rich Light Orange
      '#FFCCBC', // Rich Light Orange
      '#FFAB91', // Rich Light Orange
      '#BCAAA4', // Rich Light Brown
      '#D7CCC8', // Rich Light Brown
      '#F5F5F5', // Rich Light Grey
      '#EEEEEE', // Rich Light Grey
      '#E0E0E0', // Rich Light Grey
    ];
    const index = userId.charCodeAt(0) % colors.length;
    return colors[index];
  }

// Material Sort
sortData(sort: any): void {
  if (!sort.active) {
    this.updateDataSource();
    return;
  }

  // Handle custom sort behavior - cycle between asc and desc only
  const column = sort.active;
  const currentColumn = this.currentSortColumn();
  const currentDirection = this.currentSortDirection();

  let direction: 'asc' | 'desc';

  if (column === currentColumn) {
    // Same column - toggle direction
    direction = currentDirection === 'asc' ? 'desc' : 'asc';
  } else {
    // Different column - start with ascending
    direction = 'asc';
  }

  // Update sort state
  this.currentSortColumn.set(column);
  this.currentSortDirection.set(direction);

  // Sort the full data source
  this.dataSource = [...this.dataSource].sort((a, b) => {
    const isAsc = direction === 'asc';

    let aValue = this.getFieldValue(a, column);
    let bValue = this.getFieldValue(b, column);

    // Handle null/undefined values
    if (aValue === null || aValue === undefined) return isAsc ? 1 : -1;
    if (bValue === null || bValue === undefined) return isAsc ? -1 : 1;

    // Convert to strings for comparison
    const aStr = String(aValue).toLowerCase();
    const bStr = String(bValue).toLowerCase();

    if (aStr < bStr) return isAsc ? -1 : 1;
    if (aStr > bStr) return isAsc ? 1 : -1;
    return 0;
  });

  // Update paginated data source
  this.updatePaginatedDataSource();
}
}
